import { Card, CleanableInput } from "@caprikorn/dynamo-ui";
import { type FC, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { FORM_VALIDATION_MODE } from "@core/core.constants";
import { usePostBillConsolidationsFilter } from "@services/apiEcms/usePostBillConsolidationsFilter";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./BillConsolidation.module.css";
import type {
  BillConsolidationFormSubmitArguments,
  BillConsolidationProps,
} from "./BillConsolidationForm.types";
import type { BillConsolidationFormValues } from "../SearchDrawer.types";
import { useParseBillConsolidationResponse } from "../SearchDrawer.utils";
import { SearchResults } from "../SearchResults/SearchResults";

export const BillConsolidationForm: FC<BillConsolidationProps> = ({
  toggleDrawer,
  formId,
}) => {
  const { t } = useCustomTranslation("main");
  const { handleSubmit, getValues, control } =
    useForm<BillConsolidationFormValues>({
      mode: FORM_VALIDATION_MODE,
      defaultValues: {
        billConsolidationName: "",
        billConsolidationReference: "",
      },
    });

  const {
    data: billConsolidationData,
    mutate,
    isError,
    isPending,
  } = usePostBillConsolidationsFilter();

  const [currentSize, setCurrentSize] = useState(10);

  const onSubmit = ({ page, size }: BillConsolidationFormSubmitArguments) => {
    const { billConsolidationName, billConsolidationReference } = getValues();

    mutate({
      inputsPayload: {
        billConsolidationName: billConsolidationName || undefined,
        billConsolidationReference: billConsolidationReference || undefined,
      },
      paginationPayload: {
        page: page ?? 1,
        size: size ?? currentSize,
      },
    });
  };

  const parsedData = useParseBillConsolidationResponse(
    billConsolidationData?.data,
  );

  return (
    <Card className={classes.container}>
      <form
        className={classes.form}
        id={formId}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          control={control}
          name="billConsolidationName"
          render={({ field, fieldState }) => (
            <CleanableInput
              {...field}
              className={classes.input}
              data-dd-privacy="allow"
              disabled={isPending}
              hasError={Boolean(fieldState.error)}
              hint={
                fieldState.error?.message &&
                t(fieldState.error.message, { ns: "common" })
              }
              label={t("searchDrawer.form.billConsolidation.name.label")}
              placeholder={t(
                "searchDrawer.form.billConsolidation.name.placeholder",
              )}
              size="lg"
              value={field.value ?? ""}
            />
          )}
          rules={{
            deps: ["billConsolidationReference"],
            validate: (value, values) =>
              Boolean(value) ||
              Boolean(values.billConsolidationReference) ||
              "form.error.atLeastOneFieldMustBeFilled",
          }}
        />
        <Controller
          control={control}
          name="billConsolidationReference"
          render={({ field, fieldState }) => (
            <CleanableInput
              {...field}
              className={classes.input}
              data-dd-privacy="allow"
              disabled={isPending}
              hasError={Boolean(fieldState.error)}
              label={t("searchDrawer.form.billConsolidation.reference.label")}
              placeholder={t(
                "searchDrawer.form.billConsolidation.reference.placeholder",
              )}
              size="lg"
              value={field.value ?? ""}
            />
          )}
          rules={{
            deps: ["billConsolidationName"],
            validate: (value, values) =>
              Boolean(value) ||
              Boolean(values.billConsolidationName) ||
              "form.error.atLeastOneFieldMustBeFilled",
          }}
        />
      </form>
      <SearchResults
        data={parsedData}
        isError={isError}
        isLoading={isPending}
        level="billConsolidation"
        submitPageChange={(page) => onSubmit({ page })}
        submitSizeChange={(size) => {
          setCurrentSize(size);
          onSubmit({ size });
        }}
        toggleDrawer={toggleDrawer}
      />
    </Card>
  );
};
